import React from "react";
import {
    OperationModalState,
    OperationDraft,
    OperationStepPayload,
    createEmptyOperationDraft,
    OperationType,
    BookingStatus,
    TripStatus,
} from "../../service/interfaces/Pickup.interface";
import { PickupInForm } from "./PickupInForm";
import { PickupOutForm } from "./PickupOutForm";

export type PickupModalProps = {
    isOpen: boolean;
    tripId: string | number;
    operationType: OperationType;
    initialDraft?: OperationDraft;
    onClose: () => void;
    onChange?: (draft: OperationDraft) => void;
    onSubmitIn?: (payload: OperationStepPayload) => void | Promise<void>;
    onSubmitOut?: (payload: OperationStepPayload) => void | Promise<void>;
};

export const PickupModal: React.FC<PickupModalProps> = ({
    isOpen,
    tripId,
    operationType,
    initialDraft,
    onClose,
    onChange,
    onSubmitIn,
    onSubmitOut,
}) => {
    const [state, setState] = React.useState<OperationModalState>(() => ({
        isOpen,
        activeStep: 0,
        isSubmitting: false,
        validationErrors: {},
        draft: initialDraft ?? createEmptyOperationDraft({ tripId, operationType }),
    }));

    React.useEffect(() => {
        setState((prev) => ({ ...prev, isOpen }));
    }, [isOpen]);

    const setDraft = (partial: Partial<OperationDraft>) => {
        setState((prev) => {
            const draft = { ...prev.draft, ...partial } as OperationDraft;
            onChange?.(draft);
            return { ...prev, draft };
        });
    };

    const handleSubmitIn = async () => {
        setState((p) => ({ ...p, isSubmitting: true }));
        try {
            await onSubmitIn?.(state.draft.in);
            setDraft({ status: "step_in_recorded" });
            setState((p) => ({ ...p, activeStep: 1 }));
        } finally {
            setState((p) => ({ ...p, isSubmitting: false }));
        }
    };

    const handleSubmitOut = async () => {
        setState((p) => ({ ...p, isSubmitting: true }));
        try {
            await onSubmitOut?.(state.draft.out);
            setDraft({ status: "step_completed" });
            onClose();
        } finally {
            setState((p) => ({ ...p, isSubmitting: false }));
        }
    };

    if (!state.isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg w-[900px] max-h-[85vh] overflow-auto flex flex-col">
                <div className="flex justify-between items-center p-3 border-b">
                    <h2 className="text-[16px] font-[600] font-poppins text-[#444FBD]">
                        Update {operationType.charAt(0).toUpperCase() + operationType.slice(1)} • Trip {String(tripId)}
                    </h2>
                    <button onClick={onClose} className="text-gray-600 hover:text-black">✕</button>
                </div>
                <div className="flex gap-2 p-3 border-b">
                    <button
                        onClick={() => setState((p) => ({ ...p, activeStep: 0 }))}
                        className={`px-3 py-1 rounded ${state.activeStep === 0 ? "bg-[#444FBD] text-white" : "bg-white border"}`}
                    >
                        {operationType} In
                    </button>
                    <button
                        onClick={() => setState((p) => ({ ...p, activeStep: 1 }))}
                        className={`px-3 py-1 rounded ${state.activeStep === 1 ? "bg-[#444FBD] text-white" : "bg-white border"}`}
                    >
                        {operationType} Out
                    </button>
                </div>
                <div className="p-4">
                    {state.activeStep === 0 ? (
                        <PickupInForm
                            value={state.draft.in}
                            onChange={(payload: OperationStepPayload) => setDraft({ in: payload })}
                            onSubmit={handleSubmitIn}
                            submitting={state.isSubmitting}
                            bookingStatus={state.draft.bookingStatus}
                            tripStatus={state.draft.tripStatus}
                            onBookingStatusChange={(status) => setDraft({ bookingStatus: status })}
                            onTripStatusChange={(status) => setDraft({ tripStatus: status })}
                        />
                    ) : (
                        <PickupOutForm
                            value={state.draft.out}
                            onChange={(payload: OperationStepPayload) => setDraft({ out: payload })}
                            onSubmit={handleSubmitOut}
                            submitting={state.isSubmitting}
                            bookingStatus={state.draft.bookingStatus}
                            tripStatus={state.draft.tripStatus}
                            onBookingStatusChange={(status) => setDraft({ bookingStatus: status })}
                            onTripStatusChange={(status) => setDraft({ tripStatus: status })}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};


